import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
} from '@fluentui/react-components';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { TurnOnAllAlert, FullyOptimizedAlert } from './alerts';
import { FinalUrlExpansionPreview } from './final-url-expansion-preview';
import { FinalURLsEditor } from './side-panel/final-url-expansion-editor';

const getAutoGeneratedAssetsStyles = () => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  OptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  assetsOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '& label': {
      padding: '0px 0px 0px 8px',
    },
    '& .editorButton': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      paddingLeft: '28px',
      width: 'fit-content',
    },
    '& input[type="checkbox"]': {
      boxSizing: 'border-box',
      cursor: 'inherit',
      height: '100%',
      margin: '0px',
      opacity: 0,
      position: 'absolute',
      top: '0px',
    },
    '& .fui-Checkbox__indicator': {
      margin: '0px',
    },
    '& .checkboxDescription': {
      paddingLeft: '28px',
    },
  },
});

export const AutoGeneratedAssetsImpl = (props) => {
  const {
    i18n,
    finalUrlExpansions,
    options,
    onChange,
  } = props;
  const textAssetChecked = !options.autoGeneratedTextOptOut;
  const imageAssetChecked = !options.autoGeneratedImageOptOut;
  const finalUrlChecked = !options.finalUrlExpansionOptOut;

  const styles = useDefaultStyles(getAutoGeneratedAssetsStyles);
  const [toggleFinalURLsEditor, setToggleFinalURLsEditor] = useState(false);

  const onOpenFinalUrlEditor = useCallback(() => setToggleFinalURLsEditor(true), []);

  const renderAlert = useCallback(() => {
    if (textAssetChecked && imageAssetChecked && finalUrlChecked) {
      return <FullyOptimizedAlert />;
    }
    return <TurnOnAllAlert />;
  }, [textAssetChecked, imageAssetChecked, finalUrlChecked]);

  const renderFinalUrlExpansionPreview = useCallback(() => {
    if (finalUrlChecked && finalUrlExpansions && finalUrlExpansions.length > 0) {
      return (
        <FinalUrlExpansionPreview
          i18n={i18n}
          finalUrlExpansions={finalUrlExpansions}
          onChange={onChange}
          setToggleFinalURLsEditor={setToggleFinalURLsEditor}
        />
      );
    }
    return (
      <Button
        className={classNames('auto-generated-assets-panel-preview-edit', 'editorButton')}
        onClick={onOpenFinalUrlEditor}
        appearance="transparent"
        disabled={!finalUrlChecked}
      >
        {i18n.getString(_TL_('Exclude some URLs'))}
      </Button>
    );
  }, [finalUrlChecked, finalUrlExpansions, onOpenFinalUrlEditor, i18n, onChange]);

  return (
    <div className={styles.root}>
      <span>{i18n.getString(_TL_('Microsoft Advertising will automatically generate relevant assets in combination with the assets you provide to maximize your ad performance. Choose which assets you’d like us to automatically generate.'))}</span>
      { renderAlert() }
      <div className={styles.OptionContainer}>
        <div className={classNames('auto-generated-assets-checkbox-text', styles.assetsOption)}>
          <Checkbox
            size="large"
            label={i18n.getString(_TL_('Text Assets'))}
            checked={textAssetChecked}
            onChange={(ev, data) => onChange({ autoGeneratedTextOptOut: !data.checked })}
          />
          <div className="checkboxDescription">{i18n.getString(_TL_('Generate text assets in combination with assets you provide.'))}</div>
        </div>
        <div className={classNames('auto-generated-assets-checkbox-img', styles.assetsOption)}>
          <Checkbox
            size="large"
            label={i18n.getString(_TL_('Image Assets'))}
            checked={imageAssetChecked}
            onChange={(ev, data) => onChange({ autoGeneratedImageOptOut: !data.checked })}
          />
          <div className="checkboxDescription">{i18n.getString(_TL_('Generate image assets pulled from your ads, business domain, and landing page in combination with image assets you provide.'))}</div>
        </div>
        <div className={classNames('auto-generated-assets-checkbox-url', styles.assetsOption)}>
          <Checkbox
            size="large"
            label={i18n.getString(_TL_('Final URLs'))}
            checked={finalUrlChecked}
            onChange={(ev, data) => onChange({ finalUrlExpansionOptOut: !data.checked })}
          />
          <div className="checkboxDescription">{i18n.getString(_TL_('Send potential customers to the most relevant URLs on your website when it’s likely to enhance ad performance.'))}</div>
          { renderFinalUrlExpansionPreview() }
          {toggleFinalURLsEditor && (
            <FinalURLsEditor
              i18n={i18n}
              isOpen={toggleFinalURLsEditor}
              onClose={() => setToggleFinalURLsEditor(false)}
              onChange={onChange}
              finalUrlExpansions={finalUrlExpansions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

AutoGeneratedAssetsImpl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  finalUrlExpansions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  options: PropTypes.shape({
    autoGeneratedTextOptOut: PropTypes.bool,
    autoGeneratedImageOptOut: PropTypes.bool,
    finalUrlExpansionOptOut: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
